import optionsApi from "./optionsApi";
import type { GrantApproval } from "../types/GrantApproval";
import { SignOffDecision } from "../types/GrantApproval";

export async function getGrantApprovalStatus(
  grantId: string
): Promise<GrantApproval[] | null> {
  return optionsApi
    .get(`v1/grantApproval/approval-status/${grantId}`)
    .then((res) => res.data?.data ?? null)
    .catch(() => null);
}

export async function getPendingSignOffs(): Promise<GrantApproval[] | null> {
  return optionsApi
    .get("v1/grantApproval/pending-sign-offs")
    .then((res) => res.data?.data ?? null)
    .catch(() => null);
}

export interface SignOffPayload {
  decision: SignOffDecision;
  comment?: string;
}

export interface BulkApproverAction {
  grantId: string;
  action: SignOffDecision | "RECOMMEND_CHANGE";
  comment?: string;
  proposedChanges?: Record<string, any>;
}

export async function submitBulkApproverActions(
  actions: BulkApproverAction[]
): Promise<void> {
  return optionsApi
    .post("v1/grantApproval/sign-offs/bulk", { actions })
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to submit approver actions"
      );
    });
}

export async function parseBulkRecommendationUpload(
  file: string,
  grants: { id: string; identifier: string }[]
): Promise<{
  rows: {
    rowNumber: number;
    identifier: string;
    grantId?: string;
    proposedOptions?: number;
    proposedValue?: number;
    inputMode?: "OPTIONS" | "VALUE";
    optionsGranted?: number;
    valueOfOptions?: number;
    conversionSnapshot?: Record<string, any>;
    note?: string;
    errors: string[];
  }[];
  errors: string[];
}> {
  return optionsApi
    .post("v1/grantApproval/sign-offs/bulk-recommendation-upload/parse", {
      file,
      grants,
    })
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to parse recommendation upload"
      );
    });
}

export async function downloadBulkRecommendationTemplate(
  grants: {
    identifier: string;
    employeeName: string;
    grantedOptions: number;
    currentValue?: string;
    allocated?: string;
    matrixRule?: string;
    limitCheck?: string;
  }[]
): Promise<Blob> {
  return optionsApi
    .post(
      "v1/grantApproval/sign-offs/bulk-recommendation-upload/template",
      {
        grants,
      },
      { responseType: "blob" }
    )
    .then((res) => res.data)
    .catch(() => {
      throw new Error("Failed to download recommendation template");
    });
}

export async function submitSignOff(
  signOffId: string,
  payload: SignOffPayload
): Promise<void> {
  return optionsApi
    .post(`v1/grantApproval/sign-off/${signOffId}`, payload)
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to submit sign-off"
      );
    });
}

export async function overrideSignOff(
  signOffId: string,
  comment?: string
): Promise<void> {
  return optionsApi
    .post(`v1/grantApproval/sign-off/${signOffId}/override`, { comment })
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to override sign-off"
      );
    });
}

export async function resetApprovalFlow(grantId: string): Promise<void> {
  return optionsApi
    .post(`v1/grantApproval/approval-request/${grantId}/reset`, {})
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to reset approval flow"
      );
    });
}

export interface RecommendChangePayload {
  comment: string;
  proposedChanges?: Record<string, any>;
}

export async function submitRecommendChange(
  signOffId: string,
  payload: RecommendChangePayload
): Promise<void> {
  return optionsApi
    .post(`v1/grantApproval/sign-off/${signOffId}/recommend-change`, payload)
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to submit recommendation"
      );
    });
}

// A grant-approval (sign-off) row that recommended a change, and the actual
// recommendation content it made — these are two separate backend entities.
export interface GrantRecommendation {
  id: string;
  grantId: string;
  grantApprovalId: string; // the grant-approval (sign-off) row this came from
  comment: string;
  proposedChanges?: Record<string, any>;
  createdAt?: string;
  grantApproval?: {
    id: string;
    approverName: string;
    order: number;
    status: string;
    metaData?: { respondedAt?: string; [key: string]: any };
  };
}

export interface GrantsWithRecommendedChangesRes {
  grants: any[];
  recommendations: GrantRecommendation[];
}

// Without a grantId: all grants currently sitting in a recommend-change
// state (recommendations is always empty in this case).
// With a grantId: the same grants list, plus that grant's open
// recommendation records.
export async function getGrantsWithRecommendedChanges(
  grantId?: string
): Promise<GrantsWithRecommendedChangesRes | null> {
  return optionsApi
    .get(
      `v1/grantApproval/grants-with-recommended-changes${
        grantId ? `/${grantId}` : ""
      }`
    )
    .then((res) => res.data?.data ?? null)
    .catch(() => null);
}

export async function acceptRecommendedChanges(
  grantId: string,
  signOffId: string
): Promise<void> {
  return optionsApi
    .post(`v1/grantApproval/grants/${grantId}/resolve-recommendation`, {
      signOffId,
      resolution: "accept",
    })
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to accept recommendation"
      );
    });
}

export async function rejectRecommendedChanges(
  grantId: string,
  signOffId: string
): Promise<void> {
  return optionsApi
    .post(`v1/grantApproval/grants/${grantId}/resolve-recommendation`, {
      signOffId,
      resolution: "reject",
    })
    .then((res) => res.data?.data)
    .catch((err) => {
      throw new Error(
        err.response?.data?.errorRaw || "Failed to reject recommendation"
      );
    });
}
